// Odczyt kodów z aparatu w przeglądarce — bez aplikacji mobilnej.
//
// DWIE ścieżki, bo wsparcie przeglądarek jest dziurawe:
//  1. `BarcodeDetector` — wbudowany w Chrome/Edge/Samsung Internet na Androidzie: szybki, bez
//     pobierania czegokolwiek.
//  2. ZXing w czystym JavaScripcie — iPhone (Safari nie ma BarcodeDetector) i przeglądarki
//     komputerowe. Ładowany LENIWIE (osobny kawałek paczki), bo to ~400 kB, a większość wejść
//     do panelu nigdy nie otwiera aparatu.
//
// NIE używamy wersji WebAssembly (zxing-wasm): produkcyjne CSP (Caddyfile) nie ma
// 'wasm-unsafe-eval', więc taki dekoder zostałby zablokowany dopiero na produkcji.

const FORMATY = ['qr_code', 'code_128', 'ean_13', 'ean_8', 'code_39', 'upc_a', 'upc_e', 'itf', 'data_matrix'];
const ODSTEP_KLATEK_MS = 120;
const KAMERA = { video: { facingMode: { ideal: 'environment' } }, audio: false };

export class CameraError extends Error {
  constructor(code, cause) {
    super(code);
    this.code = code; // 'insecure' | 'unsupported' | 'denied' | 'nocamera' | 'busy' | 'unknown'
    this.cause = cause;
  }
}

function mapGetUserMediaError(e) {
  const name = e && e.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return new CameraError('denied', e);
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') return new CameraError('nocamera', e);
  if (name === 'NotReadableError' || name === 'TrackStartError') return new CameraError('busy', e);
  return new CameraError('unknown', e);
}

/** Czy w tej przeglądarce w ogóle da się poprosić o aparat (bez pytania użytkownika). */
export function cameraAvailability() {
  if (typeof window === 'undefined') return 'unsupported';
  // Przeglądarka odmawia aparatu na stronie bez HTTPS (wyjątek: localhost). Z telefonu w sieci
  // domowej `http://192.168…` aparat NIE zadziała, a sam błąd wygląda jak odmowa uprawnień.
  if (window.isSecureContext === false) return 'insecure';
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') return 'unsupported';
  return 'ok';
}

async function nativeDetector() {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null;
  try {
    const wspierane = await window.BarcodeDetector.getSupportedFormats();
    const formats = FORMATY.filter((f) => wspierane.includes(f));
    if (!formats.length) return null;
    return new window.BarcodeDetector({ formats });
  } catch (_) {
    return null;
  }
}

/**
 * Uruchamia aparat w `video` i woła `onCode(tekst)` przy każdym odczytanym kodzie.
 * Zwraca funkcję zatrzymującą (wyłącza aparat — lampka kamery gaśnie).
 * Rzuca `CameraError`.
 */
export async function startCameraDecoding(video, onCode) {
  const dostep = cameraAvailability();
  if (dostep !== 'ok') throw new CameraError(dostep);

  const detector = await nativeDetector();
  if (detector) {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia(KAMERA); } catch (e) { throw mapGetUserMediaError(e); }
    video.setAttribute('playsinline', '');
    video.muted = true;
    video.srcObject = stream;
    try { await video.play(); } catch (_) { /* autoplay bywa zablokowany do pierwszej klatki — detect i tak działa */ }

    let zatrzymany = false;
    let timer = null;
    const klatka = async () => {
      if (zatrzymany) return;
      try {
        if (video.readyState >= 2) {
          const kody = await detector.detect(video);
          for (const k of kody) if (k && k.rawValue) onCode(k.rawValue);
        }
      } catch (_) { /* pojedyncza nieczytelna klatka — próbujemy dalej */ }
      if (!zatrzymany) timer = setTimeout(klatka, ODSTEP_KLATEK_MS);
    };
    klatka();
    return () => {
      zatrzymany = true;
      clearTimeout(timer);
      stream.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    };
  }

  let BrowserMultiFormatReader;
  try {
    ({ BrowserMultiFormatReader } = await import('@zxing/browser'));
  } catch (e) {
    throw new CameraError('unsupported', e);
  }
  const reader = new BrowserMultiFormatReader();
  let controls;
  try {
    controls = await reader.decodeFromConstraints(KAMERA, video, (wynik) => {
      if (wynik) onCode(wynik.getText());
    });
  } catch (e) {
    throw mapGetUserMediaError(e);
  }
  return () => { try { controls.stop(); } catch (_) { /* już zatrzymany */ } };
}
